import React, { useEffect, useRef, useState } from 'react';
import {
    Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, IconButton, Tooltip,
    Tab, Tabs, Box, Button, CircularProgress
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { useReactToPrint } from "react-to-print";
import { Download, ArrowUpward, ArrowDownward, Delete } from '@mui/icons-material';
import PreviewUserResult from './PreviewUserResult';
import PreviewClientResult from './PreviewClientResult';
import LoadingScreen from '../UI/LoadingScreen';
import { deleteUserAssessment } from '../Queries';

const AssessmentSubmissions = ({ assessment, handleRefresh }) => {
    const [previewForm, setPreviewForm] = useState(false);
    const [previewClientResult, setPreviewClientResult] = useState(false);
    const [finalInterpretation, setFinalInterpretation] = useState([]);
    const [finalRecommendation, setFinalRecommendation] = useState([]);
    const [finalResult, setFinalResult] = useState([]);
    const [finalScore, setFinalScore] = useState({});
    const [submissions, setSubmissions] = useState([]);
    const [clientName, setClientName] = useState('');
    const [sortBy, setSortBy] = useState('');
    const [sortDirection, setSortDirection] = useState('asc');
    const [activeTab, setActiveTab] = useState('');
    const [tabs, setTabs] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [filteredSubmissions, setFilteredSubmissions] = useState([]);
    const printRef = useRef();

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
    });

    // Function to filter submissions based on active tab
    const filterSubmissionsByTab = (submissionsArray, activeTabValue) => {
        console.log('=== FILTERING START ===');
        console.log('Filtering submissions for tab:', activeTabValue);
        console.log('Total submissions to filter:', submissionsArray.length);
        
        if (!activeTabValue || submissionsArray.length === 0) {
            console.log('No filtering needed, returning all submissions');
            return submissionsArray;
        }

        const filtered = submissionsArray.filter((submission, index) => {
            console.log(`--- Filtering submission ${index + 1}/${submissionsArray.length} ---`);
            
            // Try different ways to access the user batch data
            let userBatch = null;
            
            // Check submission.results first
            if (submission.results?.user_details?.user_batch) {
                userBatch = submission.results.user_details.user_batch;
                console.log('Found userBatch in submission.results.user_details.user_batch:', userBatch);
            }
            // Check submission.json if it's an object
            else if (submission.json?.user_details?.user_batch) {
                userBatch = submission.json.user_details.user_batch;
                console.log('Found userBatch in submission.json.user_details.user_batch:', userBatch);
            }
            // Parse JSON if it's a string
            else if (typeof submission.json === 'string') {
                try {
                    const parsedResults = JSON.parse(submission.json);
                    userBatch = parsedResults?.user_details?.user_batch;
                    console.log('Found userBatch by parsing JSON string:', userBatch);
                } catch (e) {
                    console.log('Error parsing submission.json:', e);
                }
            }
            // Try other possible paths
            else if (submission.user_details?.user_batch) {
                userBatch = submission.user_details.user_batch;
                console.log('Found userBatch in submission.user_details.user_batch:', userBatch);
            }
            
            console.log('Final userBatch:', userBatch);
            console.log('Active tab program code:', activeTabValue);
            
            const matches = activeTabValue === userBatch;
            console.log('Filter result for this submission:', matches);
            console.log('--- End filtering submission ---');
            return matches;
        });

        console.log('=== FILTERING COMPLETE ===');
        console.log('Filtered submissions count:', filtered.length);
        console.log('Filtered submissions:', filtered.map(s => ({
            user_id: s.user_id,
            batch: s.results?.user_details?.user_batch || s.json?.user_details?.user_batch || 'unknown'
        })));
        return filtered;
    };

    useEffect(() => {
        console.log('Assessment data received:', assessment);
        console.log('Assessment results:', assessment?.assessmentResults);
        
        if (assessment?.assessmentResults && assessment.assessmentResults.length > 0) {
            try {
                const parsedSubmissions = assessment.assessmentResults;
                console.log('Parsed submissions:', parsedSubmissions);
                setSubmissions(Array.isArray(parsedSubmissions) ? parsedSubmissions : []);
            } catch (error) {
                console.error("Error parsing submissions JSON:", error);
                setSubmissions([]);
            }
        }
        else{
            console.log('No assessment results found');
            setSubmissions([]);
        }
    }, [assessment]);

    useEffect(() => {
        if (assessment?.program_code) {
            const programCodes = typeof assessment.program_code === 'string'
                ? assessment.program_code.split(',')
                : Array.isArray(assessment.program_code)
                    ? assessment.program_code
                    : [];

            setTabs(programCodes);
            setActiveTab(programCodes[0] || '');
        }
    }, [assessment]);

    // Filter submissions when activeTab or submissions change
    useEffect(() => {
        console.log('🔄 useEffect triggered - filtering submissions');
        console.log('Submissions count:', submissions.length);
        console.log('Active tab:', activeTab);
        
        if (submissions.length > 0) {
            if (activeTab) {
                console.log('Filtering submissions for active tab:', activeTab);
                const filtered = filterSubmissionsByTab(submissions, activeTab);
                console.log('Setting filtered submissions:', filtered.length);
                setFilteredSubmissions(filtered);
            } else {
                // No active tab selected, show all submissions
                console.log('No active tab, showing all submissions');
                setFilteredSubmissions(submissions);
            }
        } else {
            // No submissions available
            console.log('No submissions available, setting empty array');
            setFilteredSubmissions([]);
        }
    }, [submissions, activeTab]);

    const sortSubmissions = (column) => {
        const direction = column === sortBy && sortDirection === 'asc' ? 'desc' : 'asc';
        setSortBy(column);
        setSortDirection(direction);

        const sorted = [...submissions].sort((a, b) => {
            // Handle undefined values
            if (a[column] === undefined) return 1;
            if (b[column] === undefined) return -1;

            if (column === 'finalScore') {
                return direction === 'asc'
                    ? (a.finalScore - b.finalScore)
                    : (b.finalScore - a.finalScore);
            } else if (column === 'finalResult') {
                const aName = a.finalResult[0]?.name || '';
                const bName = b.finalResult[0]?.name || '';
                return direction === 'asc'
                    ? aName.localeCompare(bName)
                    : bName.localeCompare(aName);
            } else {
                return direction === 'asc'
                    ? (a[column] > b[column] ? 1 : -1)
                    : (a[column] < b[column] ? 1 : -1);
            }
        });

        setSubmissions(sorted);
        // Re-filter after sorting
        if (activeTab) {
            const filtered = filterSubmissionsByTab(sorted, activeTab);
            setFilteredSubmissions(filtered);
        } else {
            setFilteredSubmissions(sorted);
        }
    };

    const handlePrintReport = (submission) => {
        if (submission) {
            // setFinalResult(submission.finalResult || []);
            setFinalInterpretation(submission.results.finalInterpretation);
            setFinalRecommendation(submission.results.finalRecommendation);
            setFinalScore(submission.results.finalScore || {});
            setClientName(submission.results.user_details?.user_batch || '');
            setCurrentUser(submission.results.user_details || null);
            setPreviewForm(true);

            setTimeout(() => {
                handlePrint();
                setPreviewForm(false);
            }, 100);
        }
    };

    const handlePrintFullReport = () => {
        setClientName(activeTab);
        setPreviewClientResult(true);

        setTimeout(() => {
            handlePrint();
            setPreviewClientResult(false);
        }, 400);
    };

    const handleChangeTab = (event, newValue) => {
        console.log('🔀 Tab changed from', activeTab, 'to', newValue);
        setActiveTab(newValue);
    };

    const handleDeleteUserAssessment = async (submission) => {
        try {
            const confirmed = window.confirm("Are you sure you want to delete this assignment?");
            if (confirmed) {
                const assignment = await deleteUserAssessment(submission.id);
                handleRefresh();
            }
        } catch (error) {
            console.error('Error deleting assignment:', error);
        }
    }


    // Use the state-managed filtered submissions instead of inline filtering
    return (
        <div>
            {/* Show loading state if assessment is loading submissions */}
            {assessment?.isLoadingSubmissions ? (
                <LoadingScreen message="Loading submissions..." />
            ) : assessment?.submissionsError ? (
                <Box 
                    display="flex" 
                    flexDirection="column" 
                    alignItems="center" 
                    justifyContent="center" 
                    minHeight="300px"
                    textAlign="center"
                >
                    <Typography variant="h6" color="error" sx={{ mb: 1 }}>
                        Error Loading Submissions
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        There was an error fetching the submission data. Please try again.
                    </Typography>
                </Box>
            ) : (
                <>
                    {tabs.length > 0 && (
                        <Tabs
                            value={activeTab}
                            onChange={handleChangeTab}
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            {tabs.map((programCode, index) => (
                                <Tab key={index} label={programCode} value={programCode} />
                            ))}
                        </Tabs>
                    )}

                    {tabs.map((programCode, index) => (
                        <Box key={index} hidden={activeTab !== programCode}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" sx={{ color: '#0a0068', fontWeight: 600 }}>
                                    {programCode} - {filteredSubmissions.length} Submissions
                                </Typography>
                                {filteredSubmissions.length > 0 && (
                                    <Button
                                        variant="contained"
                                        style={{
                                            backgroundColor: "#0a0068",
                                            color: "#ffffff",
                                            textTransform: 'none'
                                        }}
                                        onClick={handlePrintFullReport}
                                        startIcon={<Download />}
                                    >
                                        Download report
                                    </Button>
                                )}
                            </Box>

                            <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        style={{ fontWeight: 700, cursor: 'pointer' }}
                                        onClick={() => sortSubmissions('user_details.fname')}
                                    >
                                        Name
                                        {sortBy === 'user_details.fname' && (
                                            sortDirection === 'asc'
                                                ? <ArrowUpward fontSize="small" style={{ color: '#808080' }} />
                                                : <ArrowDownward fontSize="small" style={{ color: '#808080' }} />
                                        )}
                                    </TableCell>

                                    <TableCell
                                        style={{ fontWeight: 700, cursor: 'pointer' }}
                                        onClick={() => sortSubmissions('user_id')}
                                    >
                                        Email ID
                                        {sortBy === 'user_id' && (
                                            sortDirection === 'asc'
                                                ? <ArrowUpward fontSize="small" style={{ color: '#808080' }} />
                                                : <ArrowDownward fontSize="small" style={{ color: '#808080' }} />
                                        )}
                                    </TableCell>

                                    <TableCell
                                        style={{ fontWeight: 700, cursor: 'pointer' }}
                                        onClick={() => sortSubmissions('finalScore')}
                                    >
                                        Overall Score
                                        {sortBy === 'finalScore' && (
                                            sortDirection === 'asc'
                                                ? <ArrowUpward fontSize="small" style={{ color: '#808080' }} />
                                                : <ArrowDownward fontSize="small" style={{ color: '#808080' }} />
                                        )}
                                    </TableCell>

                                    <TableCell
                                        style={{ fontWeight: 700, cursor: 'pointer' }}
                                        onClick={() => sortSubmissions('finalResult')}
                                    >
                                        Competency Level
                                        {sortBy === 'finalResult' && (
                                            sortDirection === 'asc'
                                                ? <ArrowUpward fontSize="small" style={{ color: '#808080' }} />
                                                : <ArrowDownward fontSize="small" style={{ color: '#808080' }} />
                                        )}
                                    </TableCell>

                                    <TableCell style={{ fontWeight: 700 }}>Recommendations</TableCell>
                                    <TableCell style={{ fontWeight: 700 }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {console.log('Filtered submissions count:', filteredSubmissions.length)}
                                {console.log('Total submissions count:', submissions.length)}
                                {filteredSubmissions.length > 0 ? (
                                    filteredSubmissions.map((submission, index) => (
                                        <TableRow key={index}>
                                            <TableCell>
                                                {submission.results?.user_details?.fname || submission.json?.user_details?.fname || 'N/A'} {submission.results?.user_details?.lname || submission.json?.user_details?.lname || ''}
                                                <br />
                                                <small style={{ color: '#666' }}>
                                                    ({submission.results?.user_details?.user_batch || submission.json?.user_details?.user_batch || 'No batch'})
                                                </small>
                                            </TableCell>
                                            <TableCell>{submission.user_id || 'N/A'}</TableCell>
                                            <TableCell>
                                                {submission.results?.finalScore || submission.json?.finalScore || 0}
                                            </TableCell>
                                            <TableCell>
                                                <Typography key={index}>
                                                    {submission.results?.finalInterpretation?.name || submission.json?.finalInterpretation?.name || 'N/A'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip
                                                    key={index}
                                                    title={
                                                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                                            <Typography variant="subtitle1" gutterBottom>
                                                                {submission.results?.finalRecommendation?.name || submission.json?.finalRecommendation?.name || 'N/A'}
                                                            </Typography>
                                                            <div style={{ color: "#ffffff !important" }} dangerouslySetInnerHTML={{ __html: submission.results?.finalRecommendation?.description || submission.json?.finalRecommendation?.description || 'No recommendations available' }} />
                                                        </div>
                                                    }
                                                    placement="right"
                                                    arrow
                                                >
                                                    <IconButton size="small">
                                                        <InfoIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <IconButton onClick={() => handlePrintReport(submission)}>
                                                    <Download fontSize="small" />
                                                </IconButton>
                                                <IconButton onClick={() => handleDeleteUserAssessment(submission)}>
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            No submissions found
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Hidden print containers */}
                    {previewForm && (
                        <div id="cust-print" style={{ display: 'none', marginTop: 20 }}>
                            <div
                                ref={printRef}
                                style={{
                                    margin: '10px',
                                    width: '1150px',
                                }}
                            >
                                {/* PreviewUserResult should be imported and used here */}
                                <PreviewUserResult
                                    assessment={assessment}
                                    finalResult={finalResult}
                                    recommendation={finalRecommendation}
                                    interpretation={finalInterpretation}
                                    finalScore={finalScore}
                                    client={clientName}
                                    currentUser={currentUser}
                                />
                            </div>
                        </div>
                    )}

                    {previewClientResult && (
                        <div style={{ display: "none" }}>
                            <div ref={printRef}>
                                {/* PreviewClientResult should be imported and used here */}
                                <PreviewClientResult
                                    assessment={assessment}
                                    client={clientName}
                                    submissions={submissions}
                                    activeTab={activeTab}
                                />
                            </div>
                        </div>
                    )}
                </Box>
            ))}
                </>
            )}
        </div>
    );
};

export default AssessmentSubmissions;